import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import LinkIcon from '@atlaskit/icon/core/link';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { i18n } from './messages';

const TIMEOUT_DELAY = 2500;

export type CopyLinkComponentProps = {
	onClick: () => void;
	isCopied: boolean;
	testId?: string;
};

const cursorDisabledStyles = xcss({
	cursor: 'default',
	border: 'none',
});

const buttonStyles = xcss({
	borderRadius: 'radius.small',
	height: `${24 / 14}em`,
	width: `${24 / 14}em`,
	paddingInlineEnd: 'space.0',
	paddingInlineStart: 'space.0',
	':hover': {
		background: token('color.background.neutral'),
	},
	':active': {
		background: token('color.background.neutral.hovered'),
	},
	':disabled': {
		background: 'transparent',
	},
});

const borderStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'radius.small',
	boxSizing: 'border-box',
	paddingInline: 'space.050',
	paddingBlock: 'space.050',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '24px',
	width: '24px',
});

export type CopyLinkProps = {
	showSuccess?: boolean;
	fullResultUrl?: string | (() => string);
	isButton?: boolean;
	testId?: string;
	onClick?: () => void;
};

export const CopyLinkButton = ({
	showSuccess,
	fullResultUrl,
	isButton,
	testId,
	onClick,
}: CopyLinkProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [tooltipLabel, setTooltipLabel] = useState(formatMessage(i18n.copyLinkTooltipLabel));
	const updateTooltip = React.useRef<() => void>();

	React.useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [tooltipLabel]);

	const copyToClipboard = async () => {
		// Call the onClick callback if it exists
		onClick?.();

		if (!fullResultUrl) {
			return;
		}
		await navigator.clipboard.writeText(
			typeof fullResultUrl === 'function' ? fullResultUrl() : fullResultUrl,
		);

		if (showSuccess) {
			setIsCopied(true);
			setTooltipLabel(formatMessage(i18n.copiedLinkTooltipLabel));
			setTimeout(() => {
				setIsCopied(false);
				setTooltipLabel(formatMessage(i18n.copyLinkTooltipLabel));
			}, TIMEOUT_DELAY);
		}
	};

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return tooltipLabel;
			}}
			position="top"
		>
			<Pressable
				isDisabled={isCopied}
				onClick={copyToClipboard}
				testId={testId}
				aria-label={formatMessage(i18n.copyLinkButtonLabel)}
				backgroundColor="color.background.neutral.subtle"
				xcss={[isButton ? buttonStyles : borderStyles, isCopied && cursorDisabledStyles]}
			>
				{isCopied ? (
					<StatusSuccessIcon
						label={formatMessage(i18n.successIconLabel)}
						color={token('color.icon.accent.green')}
					/>
				) : (
					<LinkIcon label="" color={token('color.icon.subtle')} aria-hidden="true" />
				)}
			</Pressable>
		</Tooltip>
	);
};
